import React, { useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import { Navigate } from 'react-router-dom';
import LandingPage from './LandingPage';
import UploadPage from './UploadPage';
import logger from '../utils/logger';

const HomePage = () => {
  const { isAuthenticated, user, isLoading, updateUserData } = useUser();
  
  // Refresh user data when component mounts
  useEffect(() => {
    if (isAuthenticated && updateUserData) {
      updateUserData();
    }
  }, [isAuthenticated]); // Remove updateUserData from dependencies to prevent loops
  
  // Show loading state while user data is being fetched
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="spinner mx-auto"></div>
          <p className="mt-4 text-secondaryText">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Show landing page for non-authenticated users
  if (!isAuthenticated) {
    return <LandingPage />;
  }
  
  // Check if free tier user has run out of credits
  // Check both 'free' and 'Free' for case sensitivity, and also check if tier is undefined/null
  const isFreeUser = !user?.subscriptionTier || user?.subscriptionTier?.toLowerCase() === 'free';
  const isAdmin = user?.subscriptionTier === 'admin';
  
  // Skip credit check for admin users
  if (isFreeUser && !isAdmin) {
    // Calculate remaining credits with fallback logic
    let used = 0;
    let limit = 10;
    
    // Try to get usage from user.usage.currentPeriod first
    if (user.usage?.currentPeriod) {
      used = user.usage.currentPeriod.imagesProcessed || 0;
      limit = user.usage.currentPeriod.monthlyLimit || user.limits?.imagesPerMonth || 10;
    } 
    // Fallback to direct limits if no usage data
    else if (user.limits?.imagesPerMonth) {
      limit = user.limits.imagesPerMonth;
      // If no usage data exists, assume they haven't used any credits yet
      used = 0;
    }
    
    const remaining = Math.max(0, limit - used);
    const hasCredits = remaining > 0;
    
    if (!hasCredits) {
      logger.debug('HomePage - Redirecting free user with no credits');
      // Force a hard redirect to ensure fresh page load
      window.location.href = '/account?tab=billing';
      return <Navigate to="/account?tab=billing" replace />;
    }
  }
  
  // Show upload page for authenticated users with credits
  return <UploadPage />;
};

export default HomePage;